import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';

// Create the WizardContext
const WizardContext = createContext();

// Custom hook to use the WizardContext
export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

export const WizardProvider = ({ children }) => {
  // --- Default Values ---
  const defaultWelcome = 'Hello! How can I help you today?';
  const defaultTone = 'friendly';
  const defaultCreativity = 'balanced';
  const defaultBotType = 'text';

  // --- State Hooks ---
  const [sessionId, setSessionId] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [botType, setBotType] = useState(defaultBotType);
  const [botTypeError, setBotTypeError] = useState('');
  const [document, setDocument] = useState(null);
  const [documentError, setDocumentError] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [filePath, setFilePath] = useState(null);
  const [welcomeMessage, setWelcomeMessage] = useState(defaultWelcome);
  const [welcomeMessageError, setWelcomeMessageError] = useState('');
  const [chatbotTone, setChatbotTone] = useState(defaultTone);
  const [chatbotToneError, setChatbotToneError] = useState('');
  const [botName, setBotName] = useState('');
  const [botNameError, setBotNameError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageError, setImageError] = useState('');
  const [creativityLevel, setCreativityLevel] = useState(defaultCreativity);
  const [creativityError, setCreativityError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [savedBotId, setSavedBotId] = useState(null); // Added
  const [botCount, setBotCount] = useState(0); // Added

  // --- Reset Function ---
  const resetWizardContext = useCallback(() => {
    console.log("Resetting Wizard Context State");
    setSessionId('');
    setActiveStep(0);
    setBotType(defaultBotType);
    setBotTypeError('');
    setDocument(null);
    setDocumentError('');
    setExtractedText('');
    setFilePath(null);
    setWelcomeMessage(defaultWelcome);
    setWelcomeMessageError('');
    setChatbotTone(defaultTone);
    setChatbotToneError('');
    setBotName('');
    setBotNameError('');
    setImageFile(null);
    setImagePreview(null);
    setImageError('');
    setCreativityLevel(defaultCreativity);
    setCreativityError('');
    setSubmitError('');
    setSavedBotId(null); // Added
    setBotCount(0); // Added
    sessionStorage.removeItem('sessionId'); // Clear sessionId from sessionStorage
    sessionStorage.removeItem('activeDocId'); // Clear activeDocId if used
  }, [defaultBotType, defaultWelcome, defaultTone, defaultCreativity]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({
    sessionId,
    setSessionId,
    activeStep,
    setActiveStep,
    botType,
    setBotType,
    botTypeError,
    setBotTypeError,
    document,
    setDocument,
    documentError,
    setDocumentError,
    extractedText,
    setExtractedText,
    filePath,
    setFilePath,
    welcomeMessage,
    setWelcomeMessage,
    welcomeMessageError,
    setWelcomeMessageError,
    chatbotTone,
    setChatbotTone,
    chatbotToneError,
    setChatbotToneError,
    botName,
    setBotName,
    botNameError,
    setBotNameError,
    imageFile,
    setImageFile,
    imagePreview,
    setImagePreview,
    imageError,
    setImageError,
    creativityLevel,
    setCreativityLevel,
    creativityError,
    setCreativityError,
    submitError,
    setSubmitError,
    savedBotId, // Added
    setSavedBotId, // Added
    botCount, // Added
    setBotCount, // Added
    resetWizardContext, // Include reset function
  }), [
    sessionId,
    activeStep,
    botType,
    botTypeError,
    document,
    documentError,
    extractedText,
    filePath,
    welcomeMessage,
    welcomeMessageError,
    chatbotTone,
    chatbotToneError,
    botName,
    botNameError,
    imageFile,
    imagePreview,
    imageError,
    creativityLevel,
    creativityError,
    submitError,
    savedBotId, // Added
    botCount, // Added
    resetWizardContext,
  ]);

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};